import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { PinInput } from './PinInput'
import { Keypad } from './Keypad'

interface LoginFormProps {
  onLoginSuccess: () => void
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const { login, isLoading, error, clearError } = useAuth()
  const navigate = useNavigate()
  const [pin, setPin] = useState('')

  const CONFIG = { maxPinLength: 8, minPinLength: 4 }

  useEffect(() => {
    if (error) {
      const t = setTimeout(clearError, 3000)
      return () => clearTimeout(t)
    }
  }, [error, clearError])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') handleKeyPress(e.key)
      else if (e.key === 'Enter') handleLogin()
      else if (e.key === 'Backspace') handleDelete()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [pin])

  const handleKeyPress = (key: string) => {
    if (pin.length < CONFIG.maxPinLength) setPin(p => p + key)
  }
  const handleClear  = () => { setPin(''); clearError() }
  const handleDelete = () => setPin(p => p.slice(0, -1))

  const handleLogin = async () => {
    if (pin.length < CONFIG.minPinLength || isLoading) return
    const success = await login(pin)
    if (success) {
      onLoginSuccess()
      setTimeout(() => {
        const routes: Record<string, string> = {
          admin: '/dashboard/admin', sales: '/dashboard/sales',
          teamleader: '/dashboard/teamleader', forklift: '/dashboard/forklift',
          technical: '/dashboard/technical',
        }
        const role = localStorage.getItem('userRole') ?? ''
        if (routes[role]) navigate(routes[role], { replace: true })
      }, 100)
    }
  }

  return (
    <div
      className="w-full login-card flex flex-col items-center p-6 sm:p-8 gap-0"
    >
      <div className="w-full mb-7">
        <p className="eyebrow">Welkom terug</p>
        <h2 className="text-2xl sm:text-[28px] font-semibold tracking-[-0.035em] mt-2" style={{ color: 'var(--text-primary)' }}>Log in op je werkplek</h2>
        <p className="text-sm mt-2 leading-6" style={{ color: 'var(--text-secondary)' }}>Voer je persoonlijke pincode in om door te gaan.</p>
      </div>

      {/* PIN dots */}
      <PinInput
        value={pin}
        onChange={setPin}
        maxLength={CONFIG.maxPinLength}
        error={!!error}
      />

      {/* Keypad */}
      <Keypad
        onKeyPress={handleKeyPress}
        onClear={handleClear}
        onDelete={handleDelete}
      />

      {/* Login button */}
      <button
        type="button"
        onClick={handleLogin}
        disabled={isLoading || pin.length < CONFIG.minPinLength}
        className="btn-primary w-full h-12 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
            </svg>
            Bezig…
          </span>
        ) : 'Inloggen'}
      </button>

      {error && (
        <p className="mt-3 text-red-500 text-xs text-center font-medium">{error}</p>
      )}

      <button
        type="button"
        onClick={() => alert('📩 Neem contact op met de systeembeheerder.')}
        className="mt-5 text-xs font-medium transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--kvh-red)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary)')}
      >
        Pincode vergeten?
      </button>
    </div>
  )
}
